// helpers
import { getGlobalThis } from 'shared/helpers/utils';
import { log } from 'shared/helpers/newRelic';
import { getProcessedUrl, getRequestBody } from './helpers';
import {
  captureError,
  captureRequest,
  captureResponse,
  captureResponseError,
} from 'shared/helpers/logger';
import { isAPIBlocked } from 'shared/components/VisitorPriortization/helpers/csr';
import { getStoredUserFormKey } from './authFormKeyStorage';
import { fetchTimeout } from 'shared/helpers/apiTimeout';
import { syncGrahakAuthCookie } from './grahakCookie';
import { getCachedCustomerData } from 'shared/helpers/userCache';
import {
  getRefreshFailureCount as getPersistedRefreshFailureCount,
  incrementRefreshFailureCount as incrementPersistedRefreshFailureCount,
  isAuthRecoveryInProgress,
  isRecoveryBlocked,
  resetAuthRecoveryState,
  resetRefreshFailureCount,
} from './authRecoveryState';
import { isAuthenticatedSessionMarked, isGuestSessionMarked } from 'shared/api/grahakSession';
import { isEligibleforGrahakRuleFromState } from 'shared/api/grahakAuthSurface';
import { getAuthAPI } from 'shared/api/authResolverLoader';
import { trackGrahakApiRequest, trackGrahakFunctionality } from 'shared/helpers/grahakMonitoring';
import { getMulticouponHeaderForUrl } from './multicouponConfig';
import { dispatchApiStoreAction, getApiStoreState } from './storeBridge';
import { authExpired } from 'shared/store/auth/authExpiredAction';
import { getDeviceType } from 'shared/components/Analytics/Initialize/helpers';

// defs
import { IApiHelperOptions } from './types';

// constants
import { ERROR_TAGS, TAG_IDENTIFIER } from 'shared/helpers/newRelic/constants';
import { getRemoteConfig } from 'shared/store/remoteConfigs/selectors';

const DEFAULT_HEADERS = {};
const DEFAULT_MAX_REFRESH_FAILURES = 2;
const MAX_REFRESH_FAILURES = 10;
/**
 * true: refresh attempted and succeeded
 * false: refresh attempted and failed
 * null: refresh intentionally skipped (legacy/non-Grahak/no-op)
 */
type RefreshAttemptResult = boolean | null;
type RefreshError = {
  statusCode?: number;
};
type RefreshRuntimeState = {
  globalRefreshPromise: Promise<RefreshAttemptResult> | null;
  guestSessionInitializationCount: number;
};
declare global {
  interface Window {
    __apiHelperRefreshState?: RefreshRuntimeState;
  }
}

const getMaxRefreshFailures = () => {
  const state = getApiStoreState();
  const { maxRefreshFailures } = (state && getRemoteConfig(state, 'grahakAuth')) || {};
  const value = Number(maxRefreshFailures);
  if (!Number.isInteger(value) || value <= 0) {
    return DEFAULT_MAX_REFRESH_FAILURES;
  }

  return Math.min(value, MAX_REFRESH_FAILURES);
};

const isBrowserRuntime = __BROWSER__ && typeof window !== 'undefined';
const DEFAULT_REFRESH_RUNTIME_STATE: RefreshRuntimeState = {
  globalRefreshPromise: null,
  guestSessionInitializationCount: 0,
};

const getRefreshRuntimeState = (): RefreshRuntimeState => {
  if (!isBrowserRuntime) {
    return DEFAULT_REFRESH_RUNTIME_STATE;
  }

  const browserWindow = window;
  if (!browserWindow.__apiHelperRefreshState) {
    browserWindow.__apiHelperRefreshState = { ...DEFAULT_REFRESH_RUNTIME_STATE };
  }

  return browserWindow.__apiHelperRefreshState;
};

const getGlobalRefreshPromise = () =>
  isBrowserRuntime ? getRefreshRuntimeState().globalRefreshPromise : null;
const setGlobalRefreshPromise = (promise: Promise<RefreshAttemptResult> | null) => {
  if (isBrowserRuntime) {
    getRefreshRuntimeState().globalRefreshPromise = promise;
  }
};

const getCurrentRefreshFailureCount = () =>
  isBrowserRuntime ? getPersistedRefreshFailureCount() : 0;

const isGuestSessionInitializing = () =>
  isBrowserRuntime ? getRefreshRuntimeState().guestSessionInitializationCount > 0 : false;

const isKnownGuestUser = (): boolean => {
  if (!isBrowserRuntime) return false;

  const storeCustomerData = getApiStoreState()?.app?.user?.customerData;

  if (
    storeCustomerData !== null &&
    storeCustomerData !== undefined &&
    typeof storeCustomerData === 'object'
  ) {
    return Object.keys(storeCustomerData).length === 0;
  }

  // The explicit Grahak identity is newer and more authoritative than the
  // customer cache during boot. The cache can still contain the previous
  // logged-in user while Redux is hydrating after auth recovery.
  if (isGuestSessionMarked()) {
    return true;
  }

  const cachedCustomerData = getCachedCustomerData();
  if (
    cachedCustomerData !== null &&
    cachedCustomerData !== undefined &&
    typeof cachedCustomerData === 'object'
  ) {
    return Object.keys(cachedCustomerData).length === 0;
  }

  return false;
};

const isKnownAuthenticatedUser = (): boolean => {
  if (!isBrowserRuntime) return false;

  const storeCustomerData = getApiStoreState()?.app?.user?.customerData;
  if (
    storeCustomerData !== null &&
    storeCustomerData !== undefined &&
    typeof storeCustomerData === 'object'
  ) {
    return Object.keys(storeCustomerData).length > 0;
  }

  if (isGuestSessionMarked()) return false;
  if (isAuthenticatedSessionMarked()) return true;

  const cachedCustomerData = getCachedCustomerData();
  return Boolean(
    cachedCustomerData &&
      typeof cachedCustomerData === 'object' &&
      Object.keys(cachedCustomerData).length > 0
  );
};

const isGrahakUserRequest = (requestUrl: string): boolean => {
  try {
    return new URL(requestUrl).pathname.replace(/\/$/, '') === '/grahak-service/api/v1/user';
  } catch (e) {
    return false;
  }
};

const isInMobileAppWebview = (): boolean => {
  if (!isBrowserRuntime || typeof navigator === 'undefined') {
    return false;
  }

  const deviceType = getDeviceType();
  const hasAppUserAgent = deviceType === 'android' || deviceType === 'ios';
  const hasAppParam = new URLSearchParams(window.location.search).get('src') === 'app';

  return hasAppUserAgent || hasAppParam;
};

const notifyMobileAppAuthFailure = (): void => {
  if (!isInMobileAppWebview()) {
    return;
  }

  try {
    const deviceType = getDeviceType();
    const browserWindow = window as any;

    if (
      deviceType === 'android' &&
      typeof browserWindow.AndroidFunction?.authFailure === 'function'
    ) {
      browserWindow.AndroidFunction.authFailure();
    } else if (
      deviceType === 'ios' &&
      typeof browserWindow.webkit?.messageHandlers?.authFailure?.postMessage === 'function'
    ) {
      browserWindow.webkit.messageHandlers.authFailure.postMessage('authFailure');
    }
  } catch (error) {
    console.error('[MobileApp] Error calling authFailure():', error);
  }
};

/**
 * Notify the Redux auth middleware that the session has expired and recovery
 * (logout + guest re-init + SPA redirect) should run. The middleware owns
 * idempotency, the per-tab circuit breaker, and the actual side effects.
 */
const dispatchAuthExpired = () => {
  if (
    !isBrowserRuntime ||
    isGuestSessionInitializing() ||
    isAuthRecoveryInProgress() ||
    isKnownGuestUser() ||
    isRecoveryBlocked()
  ) {
    return;
  }

  try {
    dispatchApiStoreAction(authExpired());
  } catch (err) {
    // Store not ready (e.g. during very early boot) — nothing safe to do.
    void err;
  }
};

const recordRefreshFailure = (statusCode?: number): void => {
  if (!isBrowserRuntime) {
    return;
  }

  if (statusCode === 401 || statusCode === 403 || statusCode === 500) {
    // These refresh responses are terminal for the current authenticated
    // session. Waiting for another protected request would leave the UI
    // logged out while server/session cleanup is deferred.
    dispatchAuthExpired();
    return;
  }

  const failureCount = incrementPersistedRefreshFailureCount();
  if (failureCount >= getMaxRefreshFailures()) {
    dispatchAuthExpired();
  }
};

const isGrahakEligible = (): boolean => {
  return isEligibleforGrahakRuleFromState(getApiStoreState());
};

/**
 *
 * A wrapper over fetch API, with some defaults
 */
export const ApiHelper = (
  srcUrl: string,
  {
    method = 'GET',
    mode = 'cors',
    cache = 'no-cache',
    credentials = __BROWSER__ ? 'same-origin' : undefined,
    headers = {},
    isBodyJSON = false,
    redirect,
    referrer,
    body,
    appendCSRFHeader = true,
    signal,
  }: IApiHelperOptions = {
    method: 'GET',
    mode: 'cors',
    cache: 'no-cache',
    credentials: __BROWSER__ ? 'same-origin' : undefined,
    headers: {},
    isBodyJSON: false,
    appendCSRFHeader: true,
  },
  timeoutValue?: number
): Promise<Response> => {
  const isGrahakEnabled = isGrahakEligible();
  syncGrahakAuthCookie(isGrahakEnabled);

  const { isBlocked, message } = isAPIBlocked(srcUrl);
  if (isBlocked) {
    return Promise.reject({
      statusCode: 503,
      statusText: 'VP_BLOCKED',
      message,
    });
  }

  const formKey = getStoredUserFormKey();
  const isPostRequest = method === 'POST';
  const { url, requestBody } = getProcessedUrl({ srcUrl, formBody: body, isBodyJSON, method });

  const reqHeaders = {
    ...DEFAULT_HEADERS,
    ...(isPostRequest && body ? { 'Content-Type': 'application/x-www-form-urlencoded' } : {}),
    ...(isBodyJSON ? { 'Content-Type': 'application/json' } : {}),
    ...(appendCSRFHeader && formKey !== '' ? { 'x-csrf-token': formKey } : {}),
    ...getMulticouponHeaderForUrl(srcUrl, getApiStoreState()),
    ...headers,
    ...(isGrahakEnabled ? { 'n-access-enabled': 'true' } : {}),
  };

  const fetchOptions = {
    method,
    mode,
    cache,
    credentials,
    redirect,
    referrer,
    body: requestBody,
    headers: reqHeaders,
  };

  const _globalThis = getGlobalThis() as Window;

  const fetch = _globalThis.fetch;

  const requestStartedAt = Date.now();
  const fetchCall = signal
    ? fetchTimeout(fetch, url, fetchOptions, timeoutValue, signal)
    : fetchTimeout(fetch, url, fetchOptions, timeoutValue);
  if (__SERVER__) {
    const requestParams = { url, ...fetchOptions };
    const requestId = captureRequest(requestParams);
    fetchCall
      .then((res: Response) => {
        if (res.status >= 400) {
          captureResponseError(res, requestId);
        } else {
          captureResponse(res, requestId);
        }
      })
      .catch((err: Error) => {
        // avoiding duplicate logs
        if (err.name !== 'AbortError') {
          captureError(err, requestId, url);
        }
      });
  } else {
    fetchCall
      .then((res: Response) => {
        trackGrahakApiRequest({
          url,
          method,
          statusCode: res.status,
          durationMs: Date.now() - requestStartedAt,
        });

        if (res.status >= 400) {
          const { hostname, pathname } = new URL(url);
          log({
            errTitle: `Error in fetchCall ApiHelper: ${res.status}: ${url}`,
            tags: {
              [TAG_IDENTIFIER.ERROR]: ERROR_TAGS.API,
              [TAG_IDENTIFIER.STATUS_CODE]: String(res.status),
              [TAG_IDENTIFIER.HOSTNAME]: hostname,
              [TAG_IDENTIFIER.PATHNAME]: pathname,
            },
          });
        }
      })
      .catch((error: Error) => {
        trackGrahakApiRequest({
          url,
          method,
          durationMs: Date.now() - requestStartedAt,
          error,
        });

        // avoiding duplicate logs
        if (error.name !== 'AbortError') {
          log({
            errTitle: 'Error in fetchCall ApiHelper - res.status >= 400',
            error,
            tags: { [TAG_IDENTIFIER.ERROR]: ERROR_TAGS.API },
          });
        }
      });
  }

  let retryCount = 0;
  const MAX_RETRIES = 1;

  const retryOn401 = async (): Promise<Response> => {
    const isRefreshCall = url.includes('/jwt-token/refresh') || url.includes('/token/refresh');
    const isLogoutCall = url.includes('/logout');
    const isOtpVerification = url.includes('/login/verify') || url.includes('/otp/verify');
    const isGuestSessionCall = url.includes('/auth/session/guest');
    const isUserCall = isGrahakUserRequest(url);

    const response = await fetchCall;
    const isGuestSession = isKnownGuestUser();

    if (isRefreshCall && response.status >= 400 && ![404].includes(response.status)) {
      return response;
    }

    const isAuthError = response.status === 401 || response.status === 403;

    // The native app owns auth recovery inside webviews. Run this before the
    // `/user` guest bootstrap so an expired app session is not replaced by a
    // browser-managed guest session. Login/OTP keeps its existing recovery.
    if (
      isAuthError &&
      !isRefreshCall &&
      !isLogoutCall &&
      !isOtpVerification &&
      !url.includes('/login') &&
      !isGuestSessionCall &&
      isInMobileAppWebview()
    ) {
      notifyMobileAppAuthFailure();
      return response;
    }

    // `/user` is the session authority during boot. A 200 response preserves
    // the current legacy or Grahak context, including an existing guest cart.
    // Only a 401 for a user not known to be authenticated may create a new
    // guest session. Authenticated users continue through refresh below.
    if (
      response.status === 401 &&
      isUserCall &&
      !isKnownAuthenticatedUser() &&
      !isGuestSessionInitializing() &&
      retryCount < MAX_RETRIES
    ) {
      retryCount++;

      try {
        // A stale guest marker must not suppress re-initialization after the
        // backend has explicitly rejected that session.
        // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
        const { clearGuestInitialized } = require('shared/api/grahakSession');
        clearGuestInitialized();

        const AuthAPI = await getAuthAPI();
        const guestResult = await AuthAPI.initGuestSession({ forceGuest: true });
        if (guestResult?.status === 'skipped') {
          return response;
        }

        return signal
          ? await fetchTimeout(fetch, url, fetchOptions, timeoutValue, signal)
          : await fetchTimeout(fetch, url, fetchOptions, timeoutValue);
      } catch (err) {
        console.error('[Auth] Failed to initialize guest session after /user 401:', err);
        return response;
      }
    }

    const isServerErrorOnOtpVerify =
      (isOtpVerification || url.includes('/login')) &&
      response.status >= 500 &&
      response.status < 600;

    if (
      ((isAuthError && (isOtpVerification || url.includes('/login'))) ||
        isServerErrorOnOtpVerify) &&
      !isGuestSessionInitializing() &&
      isBrowserRuntime
    ) {
      // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
      const { clearGuestInitialized } = require('shared/api/grahakSession');
      clearGuestInitialized();

      try {
        const AuthAPI = await getAuthAPI();
        await AuthAPI.initGuestSession({ forceGuest: true });
      } catch (err) {
        console.error('[Auth] Failed to initialize guest session:', err);
      }

      return response;
    }

    if (
      isAuthError &&
      !isRefreshCall &&
      !isLogoutCall &&
      !isOtpVerification &&
      !url.includes('/login') &&
      !isGuestSessionCall &&
      !isGuestSession &&
      !isGuestSessionInitializing() &&
      !isAuthRecoveryInProgress() &&
      retryCount < MAX_RETRIES
    ) {
      retryCount++;

      if (isRecoveryBlocked()) {
        return response;
      }

      if (getCurrentRefreshFailureCount() >= getMaxRefreshFailures()) {
        dispatchAuthExpired();
        return response;
      }

      let currentRefreshPromise = getGlobalRefreshPromise();

      if (!currentRefreshPromise) {
        currentRefreshPromise = (async () => {
          try {
            if (isAuthRecoveryInProgress() || isKnownGuestUser()) {
              return null;
            }

            const AuthAPI = await getAuthAPI();
            const refreshed = await AuthAPI.refreshJwtToken();

            if (refreshed === null || refreshed === undefined) {
              // `null`/`undefined` means refresh was intentionally skipped (legacy/non-Grahak),
              // unlike `false` which means refresh attempted but failed.
              return null;
            }

            const isSuccess =
              refreshed?.status?.toLowerCase?.() === 'success' ||
              refreshed?.success === true ||
              refreshed?.response?.access_token;

            if (isSuccess) {
              return true;
            }

            trackGrahakFunctionality({
              functionality: 'refresh_token',
              outcome: 'failure',
              reason: 'unsuccessful_response',
            });
            recordRefreshFailure();
            return false;
          } catch (err) {
            console.error(`[Auth] Refresh error:`, err);
            const statusCode =
              err && typeof err === 'object' ? (err as RefreshError).statusCode : undefined;
            trackGrahakFunctionality({
              functionality: 'refresh_token',
              outcome: 'failure',
              reason: 'request_failed',
              error: err,
            });
            recordRefreshFailure(statusCode);
            return false;
          } finally {
            setGlobalRefreshPromise(null);
          }
        })();
        setGlobalRefreshPromise(currentRefreshPromise);
      }

      const refreshSucceeded = await currentRefreshPromise;

      if (refreshSucceeded === null || isKnownGuestUser()) {
        return response;
      }

      if (refreshSucceeded) {
        const retryStartedAt = Date.now();
        try {
          const retryResponse = signal
            ? await fetchTimeout(fetch, url, fetchOptions, timeoutValue, signal)
            : await fetchTimeout(fetch, url, fetchOptions, timeoutValue);
          trackGrahakApiRequest({
            url,
            method,
            statusCode: retryResponse.status,
            durationMs: Date.now() - retryStartedAt,
          });

          if (retryResponse.status === 401 || retryResponse.status === 403) {
            trackGrahakFunctionality({
              functionality: 'authenticated_request_retry',
              outcome: 'failure',
              reason: 'unauthorized_after_refresh',
              statusCode: retryResponse.status,
            });
            dispatchAuthExpired();
          } else {
            resetRefreshFailureCount();
          }

          return retryResponse;
        } catch (retryErr) {
          console.error(`[Auth] Retry error:`, retryErr);
          trackGrahakApiRequest({
            url,
            method,
            durationMs: Date.now() - retryStartedAt,
            error: retryErr,
          });
          return response;
        }
      }

      return response;
    }

    return response;
  };

  return retryOn401();
};

/**
 * Reset refresh and recovery state after authentication is known to be healthy.
 */
export const resetRefreshCooldown = (): void => {
  setGlobalRefreshPromise(null);
  resetAuthRecoveryState();
};

/**
 * Get current refresh failure count (for testing)
 */
export const getRefreshFailureCount = (): number => {
  return getCurrentRefreshFailureCount();
};

/**
 * Set the guest session initialization flag
 * This prevents auth:expired events from being dispatched during initial guest session setup
 */
export const setGuestSessionInitializing = (isInitializing: boolean): void => {
  if (isBrowserRuntime) {
    const state = getRefreshRuntimeState();
    state.guestSessionInitializationCount = Math.max(
      0,
      state.guestSessionInitializationCount + (isInitializing ? 1 : -1)
    );
  }
};

export const ApiHelperForDealsPlatform = (
  url: string,
  method = 'GET',
  params?: { [key: string]: any } | FormData,
  headers?: { [key: string]: string }
) => {
  const { isBlocked, message } = isAPIBlocked(url);
  if (isBlocked) {
    return Promise.reject({
      status: 503,
      statusText: 'VP_BLOCKED',
      message,
    });
  }

  const isPostRequest = method === 'POST';

  const reqHeaders = {
    ...DEFAULT_HEADERS,
    ...(isPostRequest ? { 'Content-Type': 'application/x-www-form-urlencoded' } : {}),
    ...headers,
  };

  const fetchOptions = {
    method,
    mode: 'cors',
    cache: 'no-cache',
    credentials: __BROWSER__ ? 'same-origin' : undefined,
    headers: reqHeaders,
    body: getRequestBody(true, params),
  };

  const _globalThis = getGlobalThis() as Window;

  const fetch = _globalThis.fetch;

  const fetchCall = fetchTimeout(fetch, url, fetchOptions);
  if (__SERVER__) {
    const requestParams = { url, ...fetchOptions };
    const requestId = captureRequest(requestParams);
    fetchCall
      .then((res: Response) => {
        if (res.status >= 400) {
          captureResponseError(res, requestId);
        } else {
          captureResponse(res, requestId);
        }
      })
      .catch((err: Error) => {
        // avoiding duplicate logs
        if (err.name !== 'AbortError') {
          captureError(err, requestId, url);
        }
      });
  } else {
    fetchCall
      .then((res: Response) => {
        if (res.status >= 400) {
          const { hostname, pathname } = new URL(url);
          log({
            errTitle: 'Error in ApiHelperForDealsPlatform - fetchCall - res.status >= 400',
            message: `${res.status}: ${url}`,
            tags: {
              [TAG_IDENTIFIER.ERROR]: ERROR_TAGS.API,
              [TAG_IDENTIFIER.STATUS_CODE]: String(res.status),
              [TAG_IDENTIFIER.HOSTNAME]: hostname,
              [TAG_IDENTIFIER.PATHNAME]: pathname,
            },
          });
        }
      })
      .catch((error: Error) => {
        // avoiding duplicate logs
        if (error.name !== 'AbortError') {
          log({
            errTitle: 'Error in ApiHelperForDealsPlatform - fetchCall - catch',
            error,
            tags: { [TAG_IDENTIFIER.ERROR]: ERROR_TAGS.API },
          });
        }
      });
  }

  return fetchCall;
};
